import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Home, Users, BarChart, Settings, Grid, Layers, FolderTree, X, DollarSign, Package
} from 'lucide-react';
import { useProfile } from '../../contexts/ProfileContext';
import ProfileImage from './ProfileImage';
import { developerMenuItems } from '../../constants/developerMenu';

export default function Sidebar({ role, onLogout, onClose, allowedMenus }) {
    const location = useLocation();
    const { profile } = useProfile();

    const isActive = (path) => {
        // Exact match for root paths
        if (path === '/dashboard' || path === '/admin' || path === '/') {
            return location.pathname === path;
        }

        // For other paths, check if current path starts with the menu path
        // but ensure it's not just a partial match
        return location.pathname === path ||
            (location.pathname.startsWith(path + '/') && path !== '/dashboard' && path !== '/admin');
    };

    // Admin menu items
    const adminMenuItems = [
        { path: '/admin', label: 'Dashboard', icon: Home, exact: true },
        { path: '/admin/users', label: 'User Management', icon: Users, exact: false },
        { path: '/admin/apps', label: 'App Management', icon: Package, exact: false },
        { path: '/admin/categories', label: 'Categories', icon: Grid, exact: false },
        { path: '/admin/subcategories', label: 'Subcategories', icon: FolderTree, exact: false },
        { path: '/admin/pricing', label: 'Pricing', icon: DollarSign, exact: false },
        { path: '/admin/logs', label: 'System Logs', icon: BarChart, exact: false },
        { path: '/admin/internal-sync', label: 'Internal Sync', icon: Layers, exact: false },
        { path: '/admin/settings', label: 'Settings', icon: Settings, exact: false },
    ];

    const baseMenuItems = role === 'admin' ? adminMenuItems : developerMenuItems;
    const menuItems = allowedMenus
        ? baseMenuItems.filter((item) => allowedMenus.has(item.label))
        : baseMenuItems;

    // More precise active check
    const isMenuItemActive = (menuPath, exact = false) => {
        if (exact) {
            return location.pathname === menuPath;
        }

        // For non-exact matches, ensure we're not matching parent routes
        if (menuPath === '/dashboard' || menuPath === '/admin') {
            return location.pathname === menuPath;
        }

        return location.pathname === menuPath || location.pathname.startsWith(menuPath + '/');
    };

    return (
        <aside className="w-64 bg-white border-r shadow-sm flex flex-col h-screen">
            <div className="p-6 flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-800">
                    {role === 'admin' ? 'Admin Panel' : 'Developer Portal'}
                </h1>
                {/* Close button for mobile */}
                {onClose && (
                    <button
                        onClick={onClose}
                        className="lg:hidden p-1 rounded-lg hover:bg-gray-100 transition-colors"
                        aria-label="Close menu"
                    >
                        <X className="w-5 h-5 text-gray-600" />
                    </button>
                )}
            </div>

            <nav className="flex-1 px-4 pb-4 overflow-y-auto">
                {menuItems.map((item) => {
                    const Icon = item.icon;
                    const active = isMenuItemActive(item.path, item.exact);

                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            onClick={onClose}
                            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all mb-1.5 ${active
                                ? role === 'admin'
                                    ? 'bg-red-50 text-red-600 font-medium'
                                    : 'bg-blue-50 text-blue-600 font-medium'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <Icon className="w-5 h-5 flex-shrink-0" />
                            <span className="truncate">{item.label}</span>
                        </Link>
                    );
                })}

                {/* Profile menu item - Only for non-admin users */}
                {role !== 'admin' && (
                    <Link
                        to="/dashboard/profile"
                        onClick={onClose}
                        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all mb-1.5 ${location.pathname === '/dashboard/profile'
                            ? 'bg-blue-50 text-blue-600 font-medium'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        <ProfileImage
                            imageUrl={profile?.profile_image_url}
                            alt="Profile"
                            size="sm"
                            cacheBust={true}
                        />
                        <span className="truncate">My Profile</span>
                    </Link>
                )}
            </nav>

            {/* Logout */}
            <div className="p-4 border-t">
                <button
                    onClick={onLogout}
                    className={`w-full px-4 py-2.5 rounded-lg transition-colors font-medium ${role === 'admin'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                >
                    Logout
                </button>
            </div>
        </aside>
    );
}